import { useEffect, useState } from 'react';

export const CONSENT_KEY = 'analytics_consent';
export type ConsentValue = 'accepted' | 'rejected' | null;

/**
 * Custom event names used to communicate consent changes
 * across hook instances on the same page.
 */
export const CONSENT_ACCEPTED_EVENT = 'analytics-consent-accepted';
export const CONSENT_RESET_EVENT = 'analytics-consent-reset';

/**
 * useConsent
 *
 * Reads and writes the user's analytics consent choice from/to localStorage.
 * Dispatches custom window events so sibling hooks (useScreenTime) and other
 * components (ConsentBanner) can react to changes without prop drilling.
 */
export const useConsent = () => {
  const [consent, setConsent] = useState<ConsentValue>(
    () => (localStorage.getItem(CONSENT_KEY) as ConsentValue) ?? null,
  );

  // Listen for consent-reset triggered from elsewhere (e.g. "Privacy Settings" in footer)
  useEffect(() => {
    const handleReset = () => setConsent(null);
    window.addEventListener(CONSENT_RESET_EVENT, handleReset);
    return () => window.removeEventListener(CONSENT_RESET_EVENT, handleReset);
  }, []);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setConsent('accepted');
    // Notify useScreenTime instances to start timing immediately
    window.dispatchEvent(new Event(CONSENT_ACCEPTED_EVENT));
  };

  const reject = () => {
    localStorage.setItem(CONSENT_KEY, 'rejected');
    // Clear all stored analytics data as required
    localStorage.removeItem('minipt_screen_time_v1');
    localStorage.removeItem('minipt_behavior_session_id');
    setConsent('rejected');
  };

  /**
   * Clears the stored consent so the banner is shown again.
   * Called by the "Privacy Settings" link in the footer.
   */
  const reset = () => {
    localStorage.removeItem(CONSENT_KEY);
    setConsent(null);
    window.dispatchEvent(new Event(CONSENT_RESET_EVENT));
  };

  return { consent, accept, reject, reset };
};
